import Button from "@irati/ui/src/Button"
import {Input, Select, TextArea, ValidationButton} from "@irati/ui/src/FormItem"
import GenericModal from "@irati/ui/src/GenericModal"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import validation from "@irati/utils/validation"
import React, {FC, useState} from "react"
import {Row} from "react-bootstrap"
import {useOrganisationalApprovalMutation} from "@irati/payrollpay/hooks"
import Form from "@irati/ui/src/Form"
import Flex from "@irati/ui/src/Flex"
import {triggerErrorToast} from "@irati/ui/src/ToastAlert/triggerToast"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import adminTracking from "@irati-tracking/admin"

const NATIONAL_MINIMUM_WAGE = "national_minimum_wage"
const FIXED_TERM_CONTACT = "fixed_term_contract"
const SCHEME_LIMITATION = "scheme_limitation"
const PROBATION_SERVED = "probation_month_service_completed"
const MATERNITY_PATERNITY_ADOPTION = "maternity_paternity_adoption"
const OTHER_REASON = "other_reason"
const PERSONAL_HR_REASON = "personal_hr_reason"

const rejectionReasonOptions = [
  {label: "National minimum wage", value: NATIONAL_MINIMUM_WAGE},
  {label: "Schema limitation", value: SCHEME_LIMITATION},
  {label: "Fixed term contract", value: FIXED_TERM_CONTACT},
  {label: "Probation month service completed", value: PROBATION_SERVED},
  {label: "Date of birth", value: "dob"},
  {label: "National insurance number", value: "national_insurance_number"},
  {label: "Email address incorrect", value: "email_address_incorrect"},
  {label: "Incorrect address", value: "incorrect_address"},
  {label: "Phone number", value: "phone_number"},
  {label: "Wrong name", value: "wrong_name"},
  {label: "Cannot find person incorrect organisation", value: "cannot_find_person_incorrect_organisation"},
  {label: "Bank temporary staff", value: "bank_temporary_staff"},
  {label: "Personal HR Reason", value: PERSONAL_HR_REASON},
  {label: "Maternity/ paternity adoption", value: MATERNITY_PATERNITY_ADOPTION},
  {label: "Sick leave", value: "sick_leave"},
  {label: "Other reason", value: OTHER_REASON}
]

const monthYearSchema = validation.string("This is required").test("validDate", "Enter a date in MM-YYYY format", (input: string) => {
  const regex = /^(0[1-9]|1[0-2])-\d{4}$/
  return regex.test(input)
})

interface Props {
  orderId: string
  onApprove: () => void
  onReject: () => void
}

const ApproveRejectButtonModals: FC<Props> = ({orderId, onApprove, onReject}) => {
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectionReasons, setRejectionReasons] = useState([])
  const [rejectionComment, setRejectionComment] = useState(undefined)
  const [contractEndDate, setContractEndDate] = useState(undefined)
  const [amountAvailableToSpend, setAmountAvailableToSpend] = useState(undefined)
  const [amountAvailableToSpendMinimumWage, setAmountAvailableToSpendMinimumWage] = useState(undefined)
  const [dateToReapply, setDateToReapply] = useState(undefined)
  const [maternityDateToReapply, setMaternityDateToReapply] = useState(undefined)
  const [mutate, {load}] = useOrganisationalApprovalMutation()
  const {formatMessage} = useIntl()

  const handleApprove = () => {
    adminTracking.orderManagementApprove(orderId)
    mutate({
      variables: {
        orderId,
        status: formatMessage({id: "admin.components.orderManagement.buttonModals.accepted"})
      }
    })
      .then(() => {
        onApprove()
      })
      .catch(() => {
        triggerErrorToast(formatMessage({id: "admin.components.orderManagement.buttonModals.errors.approved"}))
      })
  }

  const handleReject = () => {
    const rejection_reasons = rejectionReasons.map((reason) => {
      if (reason.value === NATIONAL_MINIMUM_WAGE) {
        return {reason: reason.value, additional_information: Number(amountAvailableToSpendMinimumWage)}
      } else if (reason.value === SCHEME_LIMITATION) {
        return {reason: reason.value, additional_information: Number(amountAvailableToSpend)}
      } else if (reason.value === FIXED_TERM_CONTACT) {
        return {reason: reason.value, additional_information: contractEndDate}
      } else if (reason.value === PROBATION_SERVED) {
        return {reason: reason.value, additional_information: dateToReapply}
      } else if (reason.value === MATERNITY_PATERNITY_ADOPTION) {
        return {reason: reason.value, additional_information: maternityDateToReapply}
      }
      return {reason: reason.value}
    })

    const variables = {
      orderId,
      status: formatMessage({id: "admin.components.orderManagement.buttonModals.rejected"}),
      rejection_reasons,
      additional_comment: rejectionComment
    }
    adminTracking.orderManagementReject({orderId, rejection_reasons, rejectionComment})

    mutate({
      variables
    })
      .then(() => {
        setShowRejectModal(false)
        onReject()
      })
      .catch(() => {
        triggerErrorToast(formatMessage({id: "admin.components.orderManagement.buttonModals.errors.approved"}))
      })
  }

  const isAdditionalCommentRequired = rejectionReasons.find(
    (reason) =>
      reason?.value === SCHEME_LIMITATION ||
      reason?.value === FIXED_TERM_CONTACT ||
      reason?.value === PERSONAL_HR_REASON ||
      reason?.value === OTHER_REASON
  )

  return (
    <>
      <Button primary marginRight small onClick={handleApprove} load={load}>
        <Icon icon={IconTypes.tick} marginRight /> <FormattedMessage id="admin.components.orderManagement.buttonModals.buttons.approve" />
      </Button>
      <Button primary small onClick={() => setShowRejectModal(true)}>
        <Icon icon={IconTypes.cross} marginRight /> <FormattedMessage id="admin.components.orderManagement.buttonModals.buttons.reject" />
      </Button>
      <GenericModal
        title="admin.components.orderManagement.buttonModals.title"
        show={showRejectModal}
        onHide={() => setShowRejectModal(false)}
        actions={
          <Flex direction="column" gap={2}>
            <ValidationButton primary onClick={handleReject} load={load}>
              <FormattedMessage id="admin.components.orderManagement.buttonModals.rejectionModal.buttons.reject" />
            </ValidationButton>
            <Button tertiary onClick={() => setShowRejectModal(false)}>
              <FormattedMessage id="admin.components.orderManagement.buttonModals.rejectionModal.buttons.cancel" />
            </Button>
          </Flex>
        }>
        <Form>
          <Row>
            <Select
              label="admin.components.orderManagement.buttonModals.rejectionModal.fields.reason.label"
              isMulti
              options={rejectionReasonOptions}
              value={rejectionReasons}
              onChange={setRejectionReasons}
              validation={validation.array("platform.defaultValidation.required").max(3)}
            />
          </Row>
          {rejectionReasons.find((reason) => reason?.value === FIXED_TERM_CONTACT) && (
            <Row>
              <Input
                label="admin.components.orderManagement.buttonModals.rejectionModal.fields.contractEndDate.label"
                placeholder="MM-YYYY"
                value={contractEndDate}
                onChange={setContractEndDate}
                validation={monthYearSchema}
              />
            </Row>
          )}
          {rejectionReasons.find((reason) => reason?.value === SCHEME_LIMITATION) && (
            <Row>
              <Input
                label="admin.components.orderManagement.buttonModals.rejectionModal.fields.spend.label"
                type="number"
                placeholder="£0"
                value={amountAvailableToSpend}
                onChange={setAmountAvailableToSpend}
                validation={validation.string("platform.defaultValidation.required")}
              />
            </Row>
          )}
          {rejectionReasons.find((reason) => reason?.value === NATIONAL_MINIMUM_WAGE) && (
            <Row>
              <Input
                label="admin.components.orderManagement.buttonModals.rejectionModal.fields.spend.label"
                type="number"
                placeholder="£0"
                value={amountAvailableToSpendMinimumWage}
                onChange={setAmountAvailableToSpendMinimumWage}
                validation={validation.string("platform.defaultValidation.required")}
              />
            </Row>
          )}
          {rejectionReasons.find((reason) => reason?.value === PROBATION_SERVED) && (
            <Row>
              <Input
                label="admin.components.orderManagement.buttonModals.rejectionModal.fields.probation.label"
                placeholder="MM-YYYY"
                value={dateToReapply}
                onChange={setDateToReapply}
                validation={monthYearSchema}
              />
            </Row>
          )}
          {rejectionReasons.find((reason) => reason?.value === MATERNITY_PATERNITY_ADOPTION) && (
            <Row>
              <Input
                label="admin.components.orderManagement.buttonModals.rejectionModal.fields.maternity.label"
                placeholder="MM-YYYY"
                value={maternityDateToReapply}
                onChange={setMaternityDateToReapply}
                validation={monthYearSchema}
              />
            </Row>
          )}
          <Row>
            <TextArea
              label="admin.components.orderManagement.buttonModals.rejectionModal.fields.comment.label"
              name="rejectionComment"
              value={rejectionComment}
              onChange={setRejectionComment}
              validation={isAdditionalCommentRequired && validation.string("platform.defaultValidation.required")}
            />
          </Row>
        </Form>
      </GenericModal>
    </>
  )
}

export default ApproveRejectButtonModals
